import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface VesBalanceResponse {
  userId: string;
  balanceVes: number;
  lastMovementDate: string | null;
}

export interface VesMovementItem {
  id: string;
  userId: string;
  type: string;
  operador: string;
  banco: string | null;
  creditVes: number | null;
  debitVes: number | null;
  balanceVes: number;
  description: string | null;
  referenceId: string;
  status: string;
  operatorEmail: string | null;
  createdAt: string;
}

export interface VesMovementsResponse {
  items: VesMovementItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface VesMovementsQuery {
  userId: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export interface ManualCreditVesDto {
  userId: string;
  amount: number;
  reason: string;
}

export interface ManualDebitVesDto {
  userId: string;
  amount: number;
  reason: string;
}

@Injectable({
  providedIn: 'root',
})
export class WalletVesAdminService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  getBalance(userId: string): Observable<{ success: boolean; data: VesBalanceResponse }> {
    const url = `${this.baseUrl}wallet-ves/balance`;
    const params = new HttpParams().set('userId', userId);
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: VesBalanceResponse }>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  getMovements(query: VesMovementsQuery): Observable<{ success: boolean; data: VesMovementsResponse }> {
    const url = `${this.baseUrl}wallet-ves/movements`;
    let params = new HttpParams().set('userId', query.userId);
    if (query.page) params = params.set('page', query.page.toString());
    if (query.limit) params = params.set('limit', query.limit.toString());
    if (query.startDate) params = params.set('startDate', query.startDate);
    if (query.endDate) params = params.set('endDate', query.endDate);
    if (query.type) params = params.set('type', query.type);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: VesMovementsResponse }>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  manualCredit(dto: ManualCreditVesDto): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/manual-credit`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post(url, dto, { headers })),
      catchError(this.handleError),
    );
  }

  manualDebit(dto: ManualDebitVesDto): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/manual-debit`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post(url, dto, { headers })),
      catchError(this.handleError),
    );
  }

  getPendingRetiros(): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/retiros-ves/pending`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers })),
      catchError(this.handleError),
    );
  }

  approveRetiroVes(cardexId: string): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/retiros-ves/${cardexId}/approve`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post(url, {}, { headers })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          status: error.status,
          message: error.error?.message || 'Error al aprobar el retiro VES.',
        }));
      }),
    );
  }

  rejectRetiroVes(cardexId: string, reason: string): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/retiros-ves/${cardexId}/reject`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post(url, { reason }, { headers })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          status: error.status,
          message: error.error?.message || 'Error al rechazar el retiro VES.',
        }));
      }),
    );
  }

  exportMovements(userId: string, startDate?: string, endDate?: string): Observable<Blob> {
    const url = `${this.baseUrl}wallet-ves/movements/export`;
    let params = new HttpParams().set('userId', userId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get(url, { headers, params, responseType: 'blob' })),
      catchError(this.handleError),
    );
  }
}
